import { useMemo } from 'react';
import {
  Building2,
  Banknote,
  Sparkles,
  UserCog,
  ArrowUpRight,
  AlertTriangle,
} from 'lucide-react';
import { trpc } from '@/lib/platform-trpc';
import { useAuth } from '@/lib/platform-auth';
import { formatInr, monthlyRevenue } from '@/lib/plan-pricing';
import { PageHero, GridDivider, Mark4x4 } from '@edupra/shell';

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function OverviewPage() {
  const { user } = useAuth();

  const tenantsQ = trpc.tenant.list.useQuery({ page: 1, pageSize: 100 });
  const impQ = trpc.impersonation.listActive.useQuery();

  const kpis = useMemo(() => {
    const items = tenantsQ.data?.items ?? [];
    const byStatus = tenantsQ.data?.byStatus ?? {
      active: 0,
      trial: 0,
      suspended: 0,
      cancelled: 0,
    };
    const total = tenantsQ.data?.total ?? items.length;

    const monthStart = startOfMonth(new Date());
    const newThisMonth = items.filter(
      (t) => new Date(t.createdAt as unknown as string) >= monthStart,
    ).length;

    const mrr = monthlyRevenue(
      items.map((t) => ({ plan: String(t.plan), status: String(t.status) })),
    );

    return {
      totalTenants: total,
      byStatus,
      newThisMonth,
      mrr,
      activeImpersonations: impQ.data?.length ?? 0,
    };
  }, [tenantsQ.data, impQ.data]);

  const loading = tenantsQ.isLoading || impQ.isLoading;
  const error = tenantsQ.error?.message ?? impQ.error?.message ?? null;

  return (
    <div className="animate-rise">
      <PageHero
        backdrop
        eyebrow="Platform overview"
        title={`Welcome back${user?.name ? `, ${user.name.split(' ')[0]}` : ''}.`}
        description={
          <>
            Live data from <code className="font-mono">/trpc/platform</code>. Every action on
            this console is audited.
          </>
        }
      />

      <GridDivider cells={28} accent={20} className="mb-10" />

      {error && (
        <div
          className="mb-8 flex items-start gap-2 rounded-md px-3.5 py-2.5 text-[12.5px]"
          style={{
            background: 'var(--semantic-danger-bg)',
            color: 'var(--semantic-danger)',
            border:
              '1px solid color-mix(in oklch, var(--semantic-danger) 25%, transparent)',
          }}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.75} />
          <div>
            <strong className="font-semibold">Failed to load KPIs.</strong> {error}
          </div>
        </div>
      )}

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <NumberCell
          icon={<Building2 className="h-3.5 w-3.5" strokeWidth={1.75} />}
          label="Total tenants"
          value={loading ? '—' : kpis.totalTenants.toLocaleString('en-IN')}
          hint={
            loading
              ? 'Fetching…'
              : `${kpis.byStatus.active} active · ${kpis.byStatus.trial} trial · ${kpis.byStatus.suspended} suspended`
          }
          accent={{ row: 0, col: 0 }}
        />
        <NumberCell
          icon={<Banknote className="h-3.5 w-3.5" strokeWidth={1.75} />}
          label="MRR (placeholder)"
          value={loading ? '—' : formatInr(kpis.mrr)}
          hint={loading ? 'Fetching…' : 'plan price × active tenants'}
          accent={{ row: 1, col: 1 }}
        />
        <NumberCell
          icon={<Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />}
          label="New this month"
          value={loading ? '—' : kpis.newThisMonth.toLocaleString('en-IN')}
          hint={
            loading
              ? 'Fetching…'
              : `since ${startOfMonth(new Date()).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`
          }
          trend={
            !loading && kpis.newThisMonth > 0 ? (
              <span
                className="inline-flex items-center gap-0.5 text-[11px] font-semibold"
                style={{
                  color: 'var(--color-accent-600)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                growing
              </span>
            ) : undefined
          }
          accent={{ row: 2, col: 2 }}
        />
        <NumberCell
          icon={<UserCog className="h-3.5 w-3.5" strokeWidth={1.75} />}
          label="Active impersonations"
          value={loading ? '—' : kpis.activeImpersonations.toLocaleString('en-IN')}
          hint={
            loading
              ? 'Fetching…'
              : kpis.activeImpersonations === 0
                ? 'No live grants'
                : 'live operator sessions'
          }
          tone={kpis.activeImpersonations > 0 ? 'alert' : 'default'}
          accent={{ row: 3, col: 3 }}
        />
      </section>

      <section
        className="mt-12 rounded-xl p-6"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-line)',
        }}
      >
        <p
          className="text-[11px] font-semibold uppercase"
          style={{
            color: 'var(--color-accent-600)',
            letterSpacing: '0.10em',
            fontFamily: 'var(--font-mono)',
          }}
        >
          Roadmap
        </p>
        <h2
          className="mt-2 text-[18px] font-semibold"
          style={{ color: 'var(--fg-high)', letterSpacing: '-0.018em' }}
        >
          What ships next
        </h2>
        <p className="mt-2 max-w-2xl text-[13px] leading-relaxed" style={{ color: 'var(--fg-mid)' }}>
          This console is the scaffold. Agent 5 brings the Tenants list, detail, and
          provisioning flow. Agent 7 follows with Operators, Audit, and Impersonation
          management. Pick a destination from the left or hit{' '}
          <span className="ed-kbd">⌘</span>
          <span className="ed-kbd">K</span> to search.
        </p>
      </section>
    </div>
  );
}

interface NumberCellProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  hint: string;
  trend?: React.ReactNode;
  tone?: 'default' | 'alert';
  accent?: { row: number; col: number };
}

function NumberCell({ icon, label, value, hint, trend, tone = 'default', accent }: NumberCellProps) {
  return (
    <div
      className="flex flex-col gap-3 rounded-xl p-5"
      style={{
        background: 'var(--bg-surface)',
        border:
          tone === 'alert'
            ? '1px solid color-mix(in oklch, var(--semantic-warning) 50%, var(--border-line))'
            : '1px solid var(--border-line)',
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon && (
            <span style={{ color: 'var(--fg-low)' }}>{icon}</span>
          )}
          <p
            className="text-[10.5px] font-semibold uppercase"
            style={{
              color: 'var(--fg-low)',
              letterSpacing: '0.10em',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {label}
          </p>
        </div>
        {accent && (
          <span style={{ color: 'var(--fg-faint)' }} aria-hidden>
            <Mark4x4 size={18} accent={accent} />
          </span>
        )}
      </div>
      <div>
        <p
          style={{
            fontSize: '1.875rem',
            fontWeight: 600,
            letterSpacing: '-0.032em',
            color: 'var(--fg-high)',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.05,
          }}
        >
          {value}
        </p>
        <div
          className="mt-1.5 flex items-center justify-between gap-2 text-[11.5px]"
          style={{ color: 'var(--fg-low)' }}
        >
          <span>{hint}</span>
          {trend}
        </div>
      </div>
    </div>
  );
}
